import React, { useState, useMemo } from 'react';
import { Table, Button, Badge, Form } from 'react-bootstrap';
import { format } from 'date-fns';
import '../styles/OrderList.css';

export default function ManufacturingBacklog({ 
  orders, 
  loading, 
  onSchedule,
  toggleRow,
  expandedRows,
  renderExpandedRow,
  isOrderDirty,
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrderType, setSelectedOrderType] = useState('All');

  const COL_SPAN = 14;

  // 1. Corrected to use 'orders' prop
  const orderTypes = useMemo(() => {
    const types = new Set(orders.map(o => o.orderType).filter(Boolean));
    return ['All', ...Array.from(types).sort()];
  }, [orders]);

  // 2. Filter logic to make the search bar actually work
  const filteredOrders = useMemo(() => {
    return orders.filter(o => {
      const s = searchTerm.toLowerCase();
      const matchesSearch = 
        (o.orderId?.toLowerCase() || '').includes(s) ||
        (o.invoiceNumber?.toLowerCase() || '').includes(s) ||
        (o.customerName?.toLowerCase() || '').includes(s) ||
        (o.dealerName?.toLowerCase() || '').includes(s) ||
        (o.customWork?.toLowerCase() || '').includes(s);
      
      const matchesType = selectedOrderType === 'All' || o.orderType === selectedOrderType;

      return matchesSearch && matchesType;
    });
  }, [orders, searchTerm, selectedOrderType]);

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedOrderType('All');
  };

  if (loading) {
    return (
      <div className="text-center py-5 bg-white rounded shadow-sm">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading backlog...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="backlog-container">

      {/* Search UI Section */}
      <div className="bg-white p-3 rounded shadow-sm border mb-2 mt-3">

        <h2 className="mb-3"><i className="bi bi-list-task me-2"></i>Manufacturing Backlog</h2>
       <div className="align-items-right mb-3">
            <Badge bg="dark" pill>{filteredOrders.length} matching orders</Badge>
        </div>
        
        <div className="d-flex gap-2 w-100">
            <Form.Control 
                placeholder="Search by Invoice Number, Dealer, Customer, or Custom Work..." 
                value={searchTerm} 
                onChange={e => setSearchTerm(e.target.value)} 
            />
            <Form.Select 
                style={{ maxWidth: '180px' }} 
                value={selectedOrderType} 
                onChange={e => setSelectedOrderType(e.target.value)}
            >
                {orderTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </Form.Select>
            <Button variant="outline-secondary" onClick={handleClearFilters}>Clear</Button>
        </div>
      </div>

      {filteredOrders.length === 0 ? (
        <div className="text-center text-muted py-5 bg-white rounded shadow-sm border">
          <i className="bi bi-inbox fs-1 d-block mb-2"></i>
          No orders found matching "{searchTerm}"
        </div>
      ) : (
        <div className="shadow-sm rounded bg-white" style={{ maxHeight: '600px', overflowY: 'auto'}}>
          <Table bordered hover size="sm" className="mb-0 align-middle">
            <thead className="table-dark position-sticky top-0" style={{ zIndex: 2 }}>
              <tr>
                <th>Invoice Number</th>
                <th style={{ width: '100px' }}>Type</th>
                <th>Dealer Name</th>
                <th>Customer Name</th>
                <th style={{ minWidth: '150px' }}>Custom Work</th>
                <th style={{ width: '50px' }}>L</th>
                <th style={{ width: '50px' }}>W</th>
                <th style={{ width: '50px' }}>H</th>
                <th>Frame</th>
                <th>Upper</th>
                <th>Lower</th>
                <th>Brackets</th>
                <th>Revenue</th>
                <th>Order Date</th>
                <th className="text-center" style={{ width: '160px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {/* 3. Map over 'filteredOrders' instead of 'orders' */}
              {filteredOrders.map(order => {
                const isDirty = isOrderDirty(order);
                const isExpanded = !!expandedRows[order.orderId];

                return (
                  <React.Fragment key={order.orderId}>
                    <tr className={isDirty ? 'table-warning' : ''}>
                      <td style={{ maxWidth: '150px' }}>{order.invoiceNumber || '-'}</td>
                      <td>
                        {isDirty && <span className="text-danger me-1 fw-bold">*</span>}
                        {order.orderType || 'N/A'}
                      </td>
                      <td style={{ maxWidth: '150px' }}>{order.dealerName}</td>
                      <td style={{ maxWidth: '150px' }}>{order.customerName}</td>
                      <td className="small text-muted">{order.customWork || '---'}</td>
                      <td>{order.length ?? '-'}</td>
                      <td>{order.width ?? '-'}</td>
                      <td>{order.height ?? '-'}</td>
                      <td><small>{order.frameType || '-'}</small></td>
                      <td><small>{order.upperColor || '-'}</small></td>
                      <td><small>{order.lowerColor || '-'}</small></td>
                      <td><small>{order.brackets || '-'}</small></td>
                      <td className="fw-bold">
                        {typeof order.revenue === 'number' 
                          ? `$${order.revenue.toLocaleString()}` 
                          : '$0'}
                      </td>
                       <td>
                            <small>
                                {order.orderDate
                                ? format(new Date(order.orderDate), 'MMM d, yyyy')
                                : '-'}
                            </small>
                        </td>
                      <td>
                        <div className="d-flex flex-column gap-2 justify-content-center">
                          <Button 
                            size="sm" 
                            variant="primary" 
                            onClick={() => onSchedule(order)}
                          >
                            Schedule
                          </Button>
                          <Button 
                            size="sm" 
                            variant={isExpanded ? "secondary" : "outline-primary"}
                            onClick={() => toggleRow(order.orderId)}
                          >
                            {isExpanded ? 'Close Tasks' : 'View Tasks'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && renderExpandedRow(order, COL_SPAN)}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}